import { z } from 'zod';

import { apiFetch } from './http';

const hardeningCheckSchema = z.object({
  id: z.string(),
  requirement: z.string(),
  pass: z.boolean(),
  detail: z.string(),
});

const realmRowSchema = z.object({
  realm: z.string(),
  displayName: z.string().optional(),
  enabled: z.boolean(),
  identityProviders: z.array(z.string()),
  groupCount: z.number().int().nonnegative(),
  hardening: z.object({
    passed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    failing: z.array(z.string()),
  }),
});

const clientSummarySchema = z.object({
  clientId: z.string(),
  name: z.string().optional(),
  enabled: z.boolean(),
  publicClient: z.boolean(),
  standardFlowEnabled: z.boolean(),
  implicitFlowEnabled: z.boolean(),
  directAccessGrantsEnabled: z.boolean(),
  serviceAccountsEnabled: z.boolean(),
  redirectUris: z.array(z.string()),
  webOrigins: z.array(z.string()),
});

const identityProviderSchema = z.object({
  alias: z.string(),
  providerId: z.string(),
  enabled: z.boolean(),
  trustEmail: z.boolean(),
  hideOnLogin: z.boolean(),
});

const groupSummarySchema = z.object({
  name: z.string(),
  path: z.string(),
  precedence: z.number().int().nonnegative().optional(),
  precedenceRaw: z.string().optional(),
  neverPrimary: z.boolean(),
});

const realmDetailSchema = z.object({
  realm: z.string(),
  enabled: z.boolean(),
  settings: z.object({
    registrationAllowed: z.boolean(),
    resetPasswordAllowed: z.boolean(),
    verifyEmail: z.boolean(),
    loginWithEmailAllowed: z.boolean(),
    bruteForceProtected: z.boolean(),
    sslRequired: z.string(),
    loginTheme: z.string().optional(),
  }),
  hardening: z.array(hardeningCheckSchema),
  identityProviders: z.array(identityProviderSchema),
  groups: z.array(groupSummarySchema),
  clients: z.array(clientSummarySchema),
});

const driftFindingSchema = z.object({
  severity: z.enum(['high', 'medium', 'low']),
  realm: z.string(),
  subject: z.string(),
  finding: z.string(),
});

const driftReportSchema = z.object({
  findings: z.array(driftFindingSchema),
  declaredRealms: z.array(z.string()),
  source: z.object({
    repository: z.string(),
    path: z.string(),
    stateKey: z.string(),
  }),
});

const secretReferenceSchema = z.string().regex(
  /^arn:aws[a-zA-Z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:[A-Za-z0-9/_+=.@-]+$/,
);

export const ztrustGroupIntentSchema = z
  .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    name: z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/),
    parentKey: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/).optional(),
    precedence: z.number().int().nonnegative().optional(),
    neverPrimary: z.boolean().default(false),
  })
  .strict();

export const ztrustClientIntentSchema = z
  .object({
    clientId: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
    accessType: z.enum(['PUBLIC', 'CONFIDENTIAL', 'BEARER-ONLY']),
    enabled: z.boolean().default(true),
    standardFlowEnabled: z.boolean().default(true),
    implicitFlowEnabled: z.literal(false),
    directAccessGrantsEnabled: z.literal(false),
    serviceAccountsEnabled: z.boolean().default(false),
    pkceCodeChallengeMethod: z.literal('S256').optional(),
    redirectUris: z.array(z.string().url()).default([]),
    webOrigins: z.array(z.string().url()).default([]),
    secretReference: secretReferenceSchema.optional(),
  })
  .strict();

const desiredStateSchema = z
  .object({
    tenantId: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
    realm: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
    templateVersion: z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/),
    broker: z
      .object({
        alias: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
        providerId: z.literal('oidc'),
        displayName: z.string().min(1).max(100),
        authorizationUrl: z.string().url(),
        tokenUrl: z.string().url(),
        userInfoUrl: z.string().url(),
        issuer: z.string().url(),
        jwksUrl: z.string().url(),
        clientId: z.string().min(1),
        secretReference: secretReferenceSchema,
        defaultScopes: z.array(z.string()).min(1),
        trustEmail: z.boolean(),
      })
      .strict(),
    groups: z.array(ztrustGroupIntentSchema).min(1),
    clients: z.array(ztrustClientIntentSchema).min(1),
    hardening: z
      .object({
        registrationAllowed: z.literal(false),
        resetPasswordAllowed: z.literal(false),
        verifyEmail: z.literal(true),
        bruteForceProtected: z.literal(true),
        sslRequired: z.literal('all'),
        loginTheme: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export const ztrustRegistrationInputSchema = desiredStateSchema
  .extend({ idempotencyKey: z.string().uuid() })
  .strict();

const registrationSchema = z.object({
  registrationId: z.string(),
  tenantId: z.string(),
  realm: z.string(),
  templateVersion: z.string(),
  desiredState: desiredStateSchema,
  desiredStateHash: z.string(),
  status: z.enum(['pending-approval', 'approved']),
  revision: z.number().int().positive(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  approvedBy: z.string().optional(),
  approvedAt: z.string().datetime().optional(),
});

const registrationCreateResponseSchema = z.object({
  registration: registrationSchema,
  idempotentReplay: z.boolean(),
});
const registrationListResponseSchema = z.object({
  registrations: z.array(registrationSchema),
});
const registrationDetailResponseSchema = z.object({ registration: registrationSchema });
const registrationApprovalResponseSchema = z.object({
  registration: registrationSchema,
  idempotentReplay: z.boolean(),
  provisioningTriggered: z.literal(false),
});

export type ZtrustRegistrationInput = z.infer<typeof ztrustRegistrationInputSchema>;

export type RealmRow = z.infer<typeof realmRowSchema>;
export type RealmDetail = z.infer<typeof realmDetailSchema>;
export type DriftFinding = z.infer<typeof driftFindingSchema>;
export type DriftReport = z.infer<typeof driftReportSchema>;
export type ZtrustRegistration = z.infer<typeof registrationSchema>;

export async function listRealms(): Promise<{ realms: RealmRow[] }> {
  const response = await apiFetch<unknown>('/ops/ztrust/realms');
  return z.object({ realms: z.array(realmRowSchema) }).parse(response);
}

export async function getRealm(realm: string): Promise<RealmDetail> {
  const response = await apiFetch<unknown>(
    `/ops/ztrust/realms/${encodeURIComponent(realm)}`,
  );
  return realmDetailSchema.parse(response);
}

export async function getDrift(): Promise<DriftReport> {
  const response = await apiFetch<unknown>('/ops/ztrust/drift');
  return driftReportSchema.parse(response);
}

export async function listRegistrations(): Promise<ZtrustRegistration[]> {
  const response = await apiFetch<unknown>('/ops/ztrust/registrations');
  return registrationListResponseSchema.parse(response).registrations;
}

export async function getRegistration(
  registrationId: string,
): Promise<ZtrustRegistration> {
  const response = await apiFetch<unknown>(
    `/ops/ztrust/registrations/${encodeURIComponent(registrationId)}`,
  );
  return registrationDetailResponseSchema.parse(response).registration;
}

export async function createRegistration(
  input: ZtrustRegistrationInput,
): Promise<z.infer<typeof registrationCreateResponseSchema>> {
  const validatedInput = ztrustRegistrationInputSchema.parse(input);
  const response = await apiFetch<unknown>('/ops/ztrust/registrations', {
    method: 'POST',
    body: JSON.stringify(validatedInput),
  });
  return registrationCreateResponseSchema.parse(response);
}

export async function approveRegistration(
  registration: Pick<ZtrustRegistration, 'registrationId' | 'revision' | 'desiredStateHash'>,
): Promise<z.infer<typeof registrationApprovalResponseSchema>> {
  const response = await apiFetch<unknown>(
    `/ops/ztrust/registrations/${encodeURIComponent(registration.registrationId)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({
        expectedRevision: registration.revision,
        expectedDesiredStateHash: registration.desiredStateHash,
      }),
    },
  );
  return registrationApprovalResponseSchema.parse(response);
}
